import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { inr, fdate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loading } from "@/components/pawkt/Empty";
import { ArrowLeftRight, Download, TrendingUp, TrendingDown, ChevronRight, X, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// ---- timezone-safe local date helpers (operate on YYYY-MM-DD, no UTC drift) ----
const pad = (n) => String(n).padStart(2, "0");
const isoLocal = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
const parse = (s) => { const [y, m, d] = (s || "").split("-").map(Number); return new Date(y, (m || 1) - 1, d || 1); };
const dim = (y, m) => new Date(y, m + 1, 0).getDate(); // days in month (m 0-indexed)
const addMonthsClamp = (dt, delta) => {
  const t = new Date(dt.getFullYear(), dt.getMonth() + delta, 1);
  return new Date(t.getFullYear(), t.getMonth(), Math.min(dt.getDate(), dim(t.getFullYear(), t.getMonth())));
};
const addDays = (dt, n) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate() + n);
const lenDays = (from, to) => Math.round((parse(to) - parse(from)) / 86400000) + 1;
const monthLabel = (dt) => dt.toLocaleString("en-US", { month: "short", year: "numeric" });

const MODES = [
  ["prev_month", "Previous Month"],
  ["same_last_month", "Same Period Last Month"],
  ["prev_period", "Previous Period"],
  ["custom", "Custom Date Ranges"],
];
const CLASSES = [["operating", "Operating"], ["capex", "CAPEX"], ["financial", "Financial Cost"], ["all", "All"]];
const CLASS_LABEL = { operating: "Operating expenses only.", capex: "CAPEX only.", financial: "Financial costs only.", all: "All expense types included." };
const DIMS = [["by_category", "By Category"], ["by_business_area", "By Business Area"], ["by_payment_method", "By Payment Method"]];

const computeB = (mode, aFrom, aTo) => {
  if (mode === "prev_month") {
    const f0 = parse(aFrom);
    const ref = new Date(f0.getFullYear(), f0.getMonth() - 1, 1);
    return { from: isoLocal(new Date(ref.getFullYear(), ref.getMonth(), 1)), to: isoLocal(new Date(ref.getFullYear(), ref.getMonth() + 1, 0)) };
  }
  if (mode === "same_last_month") {
    return { from: isoLocal(addMonthsClamp(parse(aFrom), -1)), to: isoLocal(addMonthsClamp(parse(aTo), -1)) };
  }
  if (mode === "prev_period") {
    const len = lenDays(aFrom, aTo);
    const bTo = addDays(parse(aFrom), -1);
    return { from: isoLocal(addDays(bTo, -(len - 1))), to: isoLocal(bTo) };
  }
  return null; // custom: user controls B
};

const pctStr = (r) => r.change_pct === null ? (r.a > 0 ? "New" : "No change") : `${r.change_pct > 0 ? "+" : ""}${r.change_pct}%`;
const keyLabel = (dimName, key) => {
  if (dimName === "by_business_area") return key ? key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()) : "General";
  if (dimName === "by_category") return (key || "other").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  return key || "Unknown";
};

export function CompareDrawer({ open, onClose, baseFilters, departments }) {
  const navigate = useNavigate();
  const curMonth = useMemo(() => {
    const d = new Date();
    return { from: isoLocal(new Date(d.getFullYear(), d.getMonth(), 1)), to: isoLocal(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
  }, []);
  const [mode, setMode] = useState("prev_month");
  const [aFrom, setAFrom] = useState(curMonth.from);
  const [aTo, setATo] = useState(curMonth.to);
  const [bFrom, setBFrom] = useState("");
  const [bTo, setBTo] = useState("");
  const [cls, setCls] = useState("operating");
  const [dimName, setDimName] = useState("by_category");
  const [drill, setDrill] = useState(null);

  // derive Period B from mode (except custom)
  useEffect(() => {
    if (mode === "custom") { if (!bFrom) { setBFrom(aFrom); setBTo(aTo); } return; }
    const b = computeB(mode, aFrom, aTo);
    if (b) { setBFrom(b.from); setBTo(b.to); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, aFrom, aTo]);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    p.set("period_a_from", aFrom); p.set("period_a_to", aTo);
    p.set("period_b_from", bFrom); p.set("period_b_to", bTo);
    p.set("expense_class", cls);
    if (baseFilters.category) p.set("category", baseFilters.category);
    if (baseFilters.department_id) p.set("department_id", baseFilters.department_id);
    if (baseFilters.payment_method) p.set("payment_method", baseFilters.payment_method);
    if (baseFilters.vendor) p.set("vendor", baseFilters.vendor);
    if (baseFilters.expense_type) p.set("expense_type", baseFilters.expense_type);
    if (baseFilters.recurring) p.set("recurring", baseFilters.recurring);
    if (baseFilters.q) p.set("q", baseFilters.q);
    return p.toString();
  }, [aFrom, aTo, bFrom, bTo, cls, baseFilters]);

  const { data, isLoading } = useQuery({
    queryKey: ["exp-comparison", params],
    queryFn: async () => (await api.get(`/expenses/comparison?${params}`)).data,
    enabled: open && !!aFrom && !!aTo && !!bFrom && !!bTo,
  });

  const rows = data?.[dimName] || [];
  const increases = rows.filter((r) => r.change > 0).slice(0, 5);
  const decreases = rows.filter((r) => r.change < 0).sort((a, b) => a.change - b.change).slice(0, 5);
  const s = data?.summary || { a: 0, b: 0, change: 0, change_pct: null };
  const lenMismatch = mode === "custom" && aFrom && aTo && bFrom && bTo && lenDays(aFrom, aTo) !== lenDays(bFrom, bTo);
  const activeFilterText = useMemo(() => {
    const parts = [];
    if (baseFilters.category) parts.push(keyLabel("by_category", baseFilters.category));
    if (baseFilters.department_id) parts.push(keyLabel("by_business_area", baseFilters.department_id));
    if (baseFilters.payment_method) parts.push(baseFilters.payment_method);
    if (baseFilters.vendor) parts.push(`vendor “${baseFilters.vendor}”`);
    if (baseFilters.expense_type) parts.push(baseFilters.expense_type);
    if (baseFilters.recurring) parts.push(baseFilters.recurring === "true" ? "recurring" : "one-off");
    if (baseFilters.q) parts.push(`“${baseFilters.q}”`);
    return parts.join(", ");
  }, [baseFilters]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/expenses/comparison/export?dimension=${dimName}&${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "expense-comparison.csv"; a.click();
      URL.revokeObjectURL(url); toast.success("Comparison exported");
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Deep-link a mover/drilldown row into the existing /expenses register (Period A + preserved context).
  const viewInRegister = (row) => {
    const p = new URLSearchParams();
    p.set("date_from", aFrom); p.set("date_to", aTo);
    if (dimName === "by_category") p.set("category", row.key);
    if (dimName === "by_business_area" && row.key) p.set("department_id", row.key);
    if (dimName === "by_payment_method" && row.key && row.key !== "Unknown") p.set("payment_method", row.key);
    // preserve classification using the register's existing filters (no new params invented)
    if (cls === "capex") p.set("is_capex", "true");
    else if (cls === "financial") p.set("expense_type", "FINANCIAL_COST");
    else if (cls === "operating") p.set("is_capex", "false");
    // preserve remaining active base filters
    if (baseFilters.payment_method && dimName !== "by_payment_method") p.set("payment_method", baseFilters.payment_method);
    if (baseFilters.vendor) p.set("vendor", baseFilters.vendor);
    if (baseFilters.expense_type && cls !== "financial") p.set("expense_type", baseFilters.expense_type);
    if (baseFilters.recurring) p.set("recurring", baseFilters.recurring);
    if (baseFilters.q) p.set("q", baseFilters.q);
    onClose();
    navigate(`/expenses?${p.toString()}`);
  };

  const Change = ({ change, change_pct, a }) => {
    const neutral = change === 0;
    const up = change > 0;
    return (
      <span className={`inline-flex flex-wrap items-center gap-1 font-semibold ${neutral ? "text-slate-400" : up ? "text-rose-600" : "text-emerald-600"}`}>
        {!neutral && (up ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />)}
        {change >= 0 ? "+" : "-"}{inr(Math.abs(change))}
        <span className="text-xs font-medium opacity-80">({pctStr({ change_pct, a })})</span>
      </span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && (setDrill(null), onClose())}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="exp-compare-modal">
        <DialogHeader><DialogTitle className="font-display text-xl font-extrabold flex items-center gap-2"><ArrowLeftRight className="h-5 w-5 text-slate-400" /> Expense Comparison</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Read-only comparison of expenses between two periods.</DialogDescription>

        {/* Controls */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
          <div className="flex flex-wrap gap-2" data-testid="exp-compare-modes">
            {MODES.map(([val, label]) => (
              <button key={val} onClick={() => setMode(val)} data-testid={`exp-compare-mode-${val}`}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${mode === val ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{label}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Period A From</Label><Input type="date" data-testid="exp-compare-a-from" value={aFrom} onChange={(e) => setAFrom(e.target.value)} className="h-9 bg-white" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Period A To</Label><Input type="date" data-testid="exp-compare-a-to" value={aTo} onChange={(e) => setATo(e.target.value)} className="h-9 bg-white" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Period B From</Label><Input type="date" data-testid="exp-compare-b-from" value={bFrom} disabled={mode !== "custom"} onChange={(e) => setBFrom(e.target.value)} className="h-9 bg-white disabled:opacity-60" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1 block">Period B To</Label><Input type="date" data-testid="exp-compare-b-to" value={bTo} disabled={mode !== "custom"} onChange={(e) => setBTo(e.target.value)} className="h-9 bg-white disabled:opacity-60" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Expense type</span>
            {CLASSES.map(([val, label]) => (
              <button key={val} onClick={() => setCls(val)} data-testid={`exp-compare-class-${val}`}
                className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${cls === val ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{label}</button>
            ))}
          </div>
          <p className="text-xs text-slate-500" data-testid="exp-compare-class-label">{CLASS_LABEL[cls]}{activeFilterText ? ` · Filtered by: ${activeFilterText}` : ""}</p>
          {lenMismatch && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="exp-compare-length-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> These periods contain different numbers of days ({lenDays(aFrom, aTo)} vs {lenDays(bFrom, bTo)}). Percentage comparisons may be misleading.
            </div>
          )}
        </div>

        {isLoading ? <Loading /> : !data ? null : drill ? (
          <DrillView drill={drill} dimName={dimName} baseFilters={baseFilters} cls={cls}
            pa={data.period_a} pb={data.period_b} onBack={() => setDrill(null)} onView={viewInRegister} />
        ) : (
          <>
            {/* Header totals */}
            <div className="grid grid-cols-3 gap-3 mt-1" data-testid="exp-compare-summary">
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Period A<br />{fdate(data.period_a.from)} – {fdate(data.period_a.to)}</div><div className="mt-1 font-display text-xl font-extrabold text-slate-900" data-testid="exp-compare-total-a">{inr(s.a)}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Period B<br />{fdate(data.period_b.from)} – {fdate(data.period_b.to)}</div><div className="mt-1 font-display text-xl font-extrabold text-slate-900" data-testid="exp-compare-total-b">{inr(s.b)}</div></div>
              <div className="rounded-xl border border-slate-200 p-3"><div className="text-xs text-slate-500">Change</div><div className="mt-1 font-display text-xl" data-testid="exp-compare-total-change"><Change change={s.change} change_pct={s.change_pct} a={s.a} /></div></div>
            </div>

            {/* Dimension toggle */}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
              <div className="flex flex-wrap gap-2" data-testid="exp-compare-dims">
                {DIMS.map(([val, label]) => (
                  <button key={val} onClick={() => setDimName(val)} data-testid={`exp-compare-dim-${val}`}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${dimName === val ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>{label}</button>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="exp-compare-export-btn" className="gap-1.5"><Download className="h-4 w-4" /> Export Comparison CSV</Button>
            </div>

            {/* Breakdown */}
            {rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400" data-testid="exp-compare-empty">No expenses in either period for this selection.</p>
            ) : (
              <div className="mt-1 rounded-xl border border-slate-200 overflow-hidden" data-testid="exp-compare-table">
                <div className="hidden sm:grid grid-cols-[1.4fr_1fr_1fr_1.3fr] bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  <span>{DIMS.find((d) => d[0] === dimName)[1].replace("By ", "")}</span><span className="text-right">Period A</span><span className="text-right">Period B</span><span className="text-right">Change</span>
                </div>
                {rows.map((r) => {
                  const clickable = dimName !== "by_payment_method";
                  return (
                    <div key={r.key || "none"} onClick={() => clickable && setDrill(r)} data-testid={`exp-compare-row-${r.key || "none"}`}
                      className={`grid grid-cols-2 sm:grid-cols-[1.4fr_1fr_1fr_1.3fr] items-center gap-1 px-3 py-2.5 border-t border-slate-100 text-sm ${clickable ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                      <span className="font-medium text-slate-800 flex items-center gap-1 col-span-2 sm:col-span-1">{keyLabel(dimName, r.key)}{clickable && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}</span>
                      <span className="text-right text-slate-600 sm:block"><span className="sm:hidden text-[10px] text-slate-400">A </span>{inr(r.a)}</span>
                      <span className="text-right text-slate-600"><span className="sm:hidden text-[10px] text-slate-400">B </span>{inr(r.b)}</span>
                      <span className="text-right col-span-2 sm:col-span-1"><Change change={r.change} change_pct={r.change_pct} a={r.a} /></span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Biggest movers */}
            {(increases.length > 0 || decreases.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <Movers title="Biggest increases" rows={increases} dimName={dimName} tid="exp-compare-increases" onView={viewInRegister} />
                <Movers title="Biggest decreases" rows={decreases} dimName={dimName} tid="exp-compare-decreases" onView={viewInRegister} />
              </div>
            )}
            <p className="mt-2 text-xs text-slate-400">Descriptive comparison from the expense ledger — read-only. CAPEX and financial costs are kept separate from operating expenses.</p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Movers = ({ title, rows, dimName, tid, onView }) => (
  <div className="rounded-xl border border-slate-200 p-3" data-testid={tid}>
    <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">{title}</h4>
    {rows.length === 0 ? <p className="text-sm text-slate-400">None</p> : (
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.key || "none"} className="flex items-center justify-between gap-2 text-sm">
            <span className="text-slate-700 truncate">{keyLabel(dimName, r.key)}</span>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`font-semibold ${r.change > 0 ? "text-rose-600" : "text-emerald-600"}`}>{r.change >= 0 ? "+" : "-"}{inr(Math.abs(r.change))}</span>
              <button onClick={() => onView(r)} data-testid={`exp-compare-view-${r.key || "none"}`} title="View in expenses"
                className="text-slate-400 hover:text-slate-900 transition-colors"><ExternalLink className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

function DrillView({ drill, dimName, baseFilters, cls, pa, pb, onBack, onView }) {
  const build = (from, to) => {
    const p = new URLSearchParams();
    p.set("limit", "200"); p.set("status", "active");
    p.set("date_from", from); p.set("date_to", to);
    if (dimName === "by_category") p.set("category", drill.key);
    if (dimName === "by_business_area" && drill.key) p.set("department_id", drill.key);
    if (baseFilters.payment_method) p.set("payment_method", baseFilters.payment_method);
    if (baseFilters.vendor) p.set("vendor", baseFilters.vendor);
    if (cls === "capex") p.set("is_capex", "true");
    if (cls === "financial") p.set("expense_type", "FINANCIAL_COST");
    return p.toString();
  };
  const { data: aData } = useQuery({ queryKey: ["cmp-drill-a", dimName, drill.key, pa, cls], queryFn: async () => (await api.get(`/expenses?${build(pa.from, pa.to)}`)).data });
  const { data: bData } = useQuery({ queryKey: ["cmp-drill-b", dimName, drill.key, pb, cls], queryFn: async () => (await api.get(`/expenses?${build(pb.from, pb.to)}`)).data });

  const List = ({ title, range, items, amount }) => (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <div><div className="text-xs text-slate-500">{title}</div><div className="text-[11px] text-slate-400">{fdate(range.from)} – {fdate(range.to)}</div></div>
        <div className="font-display text-lg font-extrabold text-slate-900">{inr(amount)}</div>
      </div>
      {(!items || items.length === 0) ? <p className="text-sm text-slate-400">No expenses</p> : (
        <div className="divide-y divide-slate-100 max-h-56 overflow-y-auto">
          {items.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-1.5 text-sm">
              <span className="text-slate-600 truncate pr-2">{fdate(e.expense_date)} · {e.subcategory || e.description || keyLabel(dimName, drill.key)}</span>
              <span className="font-medium text-slate-800 shrink-0">{inr((e.amount || 0) + (e.tax || 0))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="exp-compare-drill">
      <button onClick={onBack} data-testid="exp-compare-drill-back" className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 mt-1 mb-2"><X className="h-4 w-4" /> Back to comparison</button>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-display text-lg font-extrabold text-slate-900">{keyLabel(dimName, drill.key)}</h3>
        <Button variant="outline" size="sm" onClick={() => onView(drill)} data-testid="exp-compare-drill-view" className="gap-1.5"><ExternalLink className="h-4 w-4" /> View Expenses</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <List title="Period A" range={pa} items={aData?.items} amount={drill.a} />
        <List title="Period B" range={pb} items={bData?.items} amount={drill.b} />
      </div>
    </div>
  );
}
